/**
 * Service Worker configuration for caching Sanity assets
 */

// Cache names
const SANITY_ASSET_CACHE = "sanity-assets-v1";
const SANITY_IMAGE_CACHE = "sanity-images-v1";
const SANITY_VIDEO_CACHE = "sanity-videos-v1";

// Cache duration in milliseconds
const CACHE_DURATIONS = {
  images: 7 * 24 * 60 * 60 * 1000, // 7 days
  videos: 14 * 24 * 60 * 60 * 1000, // 14 days
  assets: 30 * 24 * 60 * 60 * 1000, // 30 days
};

/**
 * Register the service worker
 */
export const registerServiceWorker = () => {
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/sw.js")
        .then((registration) => {
          console.log(
            "ServiceWorker registration successful with scope: ",
            registration.scope
          );
        })
        .catch((error) => {
          console.error("ServiceWorker registration failed: ", error);
        });
    });
  }
};

/**
 * Service Worker content to be placed in a separate sw.js file in the public directory
 */
export const serviceWorkerContent = `
// Service Worker for Sanity Asset Caching

// Cache names
const SANITY_ASSET_CACHE = 'sanity-assets-v1';
const SANITY_IMAGE_CACHE = 'sanity-images-v1';
const SANITY_VIDEO_CACHE = 'sanity-videos-v1';

// Sanity CDN domains
const SANITY_DOMAINS = [
  'cdn.sanity.io',
];

// Install event - pre-cache important assets
self.addEventListener('install', event => {
  self.skipWaiting();
});

// Activate event - clean up old caches
self.addEventListener('activate', event => {
  event.waitUntil(
    caches.keys().then(cacheNames => {
      return Promise.all(
        cacheNames.map(cacheName => {
          // Delete outdated caches
          if (
            cacheName !== SANITY_ASSET_CACHE &&
            cacheName !== SANITY_IMAGE_CACHE &&
            cacheName !== SANITY_VIDEO_CACHE &&
            cacheName.startsWith('sanity-')
          ) {
            return caches.delete(cacheName);
          }
        })
      );
    })
  );
});

// Helper to determine cache and expiration for a request
const getCacheConfig = request => {
  const url = new URL(request.url);
  
  // Check if this is a Sanity asset
  const isSanityAsset = SANITY_DOMAINS.some(domain => url.hostname.includes(domain));
  
  if (!isSanityAsset) {
    return null;
  }
  
  // Determine asset type and appropriate cache
  if (url.pathname.match(/\\.(jpg|jpeg|png|gif|webp|svg)/i)) {
    return {
      cacheName: SANITY_IMAGE_CACHE,
      expiration: Date.now() + (7 * 24 * 60 * 60 * 1000) // 7 days
    };
  } else if (url.pathname.match(/\\.(mp4|webm|ogg)/i)) {
    return {
      cacheName: SANITY_VIDEO_CACHE,
      expiration: Date.now() + (14 * 24 * 60 * 60 * 1000) // 14 days
    };
  } else if (url.pathname.includes('/files/') || url.pathname.includes('/assets/')) {
    return {
      cacheName: SANITY_ASSET_CACHE,
      expiration: Date.now() + (30 * 24 * 60 * 60 * 1000) // 30 days
    };
  }
  
  return null;
};

// Fetch event - implement cache strategy
self.addEventListener('fetch', event => {
  const request = event.request;
  
  // Only handle GET requests
  if (request.method !== 'GET') return;
  
  const cacheConfig = getCacheConfig(request);
  
  // If not a cacheable asset, pass through
  if (!cacheConfig) return;
  
  event.respondWith(
    caches.open(cacheConfig.cacheName).then(cache => {
      return cache.match(request).then(response => {
        // Check if we have a valid cached response
        if (response) {
          // Check for expiration header
          const expirationHeader = response.headers.get('sw-expiration');
          if (expirationHeader && parseInt(expirationHeader) > Date.now()) {
            // Return cached response if not expired
            return response;
          }
        }
        
        // Fetch new response if no cache or expired
        return fetch(request).then(networkResponse => {
          // Clone the response
          const clonedResponse = networkResponse.clone();
          
          // Create a new response with expiration header
          const headers = new Headers(clonedResponse.headers);
          headers.append('sw-expiration', cacheConfig.expiration.toString());
          
          const responseToCache = new Response(
            clonedResponse.body, 
            {
              status: clonedResponse.status,
              statusText: clonedResponse.statusText,
              headers
            }
          );
          
          // Cache the response
          cache.put(request, responseToCache);
          
          return networkResponse;
        }).catch(error => {
          // Return expired cache as fallback if network fails
          if (response) return response;
          throw error;
        });
      });
    })
  );
});

// Periodically clean up expired cache entries
self.addEventListener('message', event => {
  if (event.data === 'clean-expired-caches') {
    event.waitUntil(
      Promise.all([
        SANITY_ASSET_CACHE, 
        SANITY_IMAGE_CACHE, 
        SANITY_VIDEO_CACHE
      ].map(cacheName => {
        return caches.open(cacheName).then(cache => {
          return cache.keys().then(requests => {
            return Promise.all(
              requests.map(request => {
                return cache.match(request).then(response => {
                  if (response) {
                    const expirationHeader = response.headers.get('sw-expiration');
                    if (expirationHeader && parseInt(expirationHeader) < Date.now()) {
                      return cache.delete(request);
                    }
                  }
                  return Promise.resolve();
                });
              })
            );
          });
        });
      }))
    );
  }
});
`;

/**
 * Create the sw.js file in the public directory
 * This needs to be done during the build process
 */
export const createServiceWorkerFile = () => {
  // This function would be called during the build process
  // It would write the serviceWorkerContent to a file at public/sw.js
  // Implementation depends on the build system being used
  console.log("Service worker file needs to be created during build");
};

/**
 * Trigger cache cleanup
 */
export const cleanupExpiredCache = () => {
  if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage("clean-expired-caches");
  }
};

/**
 * Initialize the service worker and cache management
 */
export const initCaching = () => {
  registerServiceWorker();

  // Set up periodic cache cleanup
  setInterval(cleanupExpiredCache, 24 * 60 * 60 * 1000); // Once a day
};
